import { initializePermissionApi, PermissionApi } from '@data-delivery/permission-ui-api';
import { useMutation } from '@tanstack/react-query';
import { permissionQueryClient } from '../query-client';
import { sftpUsersKeys } from '../sftp-users/server/sftp-users-keys';
import { assignmentsKeys } from './assignments-keys';

const assignmentsApi = initializePermissionApi(PermissionApi.AssignmentsApi);

export const useCreateAssignmentMutation = () =>
  useMutation<
    string,
    unknown,
    PermissionApi.CreateAssignmentDto & { id?: string },
    { prev?: PermissionApi.AssignmentEntity[] }
  >({
    async mutationFn(dto: PermissionApi.CreateAssignmentDto & { id?: string }) {
      const id = dto.id || `${dto.businessGroup}-${dto.name}`.toLowerCase().replace(/\s+/g, '-');
      const { id: _omit, ...rest } = dto;
      await assignmentsApi.createAssignment({ createAssignmentDto: rest });
      return id;
    },
    async onMutate(dto) {
      const id = dto.id || `${dto.businessGroup}-${dto.name}`.toLowerCase().replace(/\s+/g, '-');
      await permissionQueryClient.cancelQueries({ queryKey: assignmentsKeys.list() });
      const prev = permissionQueryClient.getQueryData<PermissionApi.AssignmentEntity[]>(
        assignmentsKeys.list(),
      );
      const optimistic: PermissionApi.AssignmentEntity = {
        id,
        name: dto.name,
        description: dto.description,
        contract: dto.contract,
        startDate: dto.startDate as unknown as number,
        endDate: dto.endDate as unknown as number,
        businessGroup: dto.businessGroup,
        products: dto.products,
        services: dto.services,
        lastModified: Date.now(),
        lastModifiedBy: 'optimistic-user',
      };
      permissionQueryClient.setQueryData(assignmentsKeys.list(), [...(prev || []), optimistic]);
      return { prev };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prev) {
        permissionQueryClient.setQueryData(assignmentsKeys.list(), ctx.prev);
      }
    },
    onSuccess() {
      permissionQueryClient.invalidateQueries({ queryKey: assignmentsKeys.all() });

      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.all() });
    },
  });

export const useUpdateAssignmentMutation = () =>
  useMutation<
    string,
    unknown,
    { id: string; changes: PermissionApi.UpdateAssignmentDto },
    { prev?: PermissionApi.AssignmentEntity[] }
  >({
    async mutationFn({ id, changes }: { id: string; changes: PermissionApi.UpdateAssignmentDto }) {
      await assignmentsApi.updateAssignment({ id, updateAssignmentDto: changes });
      return id;
    },
    async onMutate({ id, changes }) {
      await permissionQueryClient.cancelQueries({ queryKey: assignmentsKeys.list() });
      const prev = permissionQueryClient.getQueryData<PermissionApi.AssignmentEntity[]>(
        assignmentsKeys.list(),
      );
      if (prev) {
        const updated = prev.map(a =>
          a.id === id ? { ...a, ...changes, lastModified: Date.now() } : a,
        );
        permissionQueryClient.setQueryData(assignmentsKeys.list(), updated);
      }

      return { prev };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prev) {
        permissionQueryClient.setQueryData(assignmentsKeys.list(), ctx.prev);
      }
    },
    onSuccess(_data, vars) {
      permissionQueryClient.invalidateQueries({ queryKey: assignmentsKeys.byId(vars.id) });
      permissionQueryClient.invalidateQueries({ queryKey: assignmentsKeys.all() });

      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.all() });
    },
  });

export const useDeleteAssignmentMutation = () =>
  useMutation<string, unknown, string, { prev?: PermissionApi.AssignmentEntity[] }>({
    async mutationFn(id: string) {
      await assignmentsApi.cascadeDeleteAssignment({ id });
      return id;
    },
    async onMutate(id) {
      await permissionQueryClient.cancelQueries({ queryKey: assignmentsKeys.list() });
      const prev = permissionQueryClient.getQueryData<PermissionApi.AssignmentEntity[]>(
        assignmentsKeys.list(),
      );
      if (prev) {
        permissionQueryClient.setQueryData(
          assignmentsKeys.list(),
          prev.filter(a => a.id !== id),
        );
      }

      return { prev };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prev) {
        permissionQueryClient.setQueryData(assignmentsKeys.list(), ctx.prev);
      }
    },
    onSuccess(returnedId) {
      permissionQueryClient.invalidateQueries({ queryKey: assignmentsKeys.all() });
      permissionQueryClient.removeQueries({ queryKey: assignmentsKeys.byId(returnedId) });

      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.all() });
    },
  });
